/**
 * One place where the app's audio sources agree that only one of them plays at
 * a time (iOS allows one media element anyway, and two voices at once is never
 * what a student wants).
 *
 * Each source registers how to make itself quiet, then claims focus right
 * before it starts. The registry imports no source itself, so a lazily loaded
 * source (chat read-aloud lives in the companion chunk) stays lazy: it only
 * registers once its module has loaded, and until then it has nothing to stop.
 */
export type MediaFocusOwner =
  'chat-read-aloud' | 'audio-overview' | 'video' | 'question-coach';

const quieters = new Map<MediaFocusOwner, () => void>();

export const registerMediaFocusOwner = (
  owner: MediaFocusOwner,
  quiet: () => void
): (() => void) => {
  quieters.set(owner, quiet);

  return () => {
    if (quieters.get(owner) === quiet) quieters.delete(owner);
  };
};

/** Quiet every registered source except `owner`. */
export const claimMediaFocus = (owner: MediaFocusOwner): void => {
  quieters.forEach((quiet, key) => {
    if (key !== owner) quiet();
  });
};
